import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Star,
  Route,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Clock,
  MapPin
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { adminRoutesApi } from '../../services/adminApi';

const AdminRoutesPage = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: '',
    places_count: 0,
    image: '',
    popular: true,
    category: '',
    rating: 4.5
  });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const data = await adminRoutesApi.getAll({ search, page, limit: 20 });
      setRoutes(data.routes);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, [search, page]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      if (editingRoute) {
        await adminRoutesApi.update(editingRoute.id, formData);
      } else {
        await adminRoutesApi.create(formData);
      }
      fetchRoutes();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save route:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (route) => {
    setEditingRoute(route);
    setFormData({
      name: route.name || '',
      description: route.description || '',
      duration: route.duration || '',
      places_count: route.places_count || 0,
      image: route.image || '',
      popular: route.popular !== false,
      category: route.category || '',
      rating: route.rating || 4.5
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu rotayı silmek istediğinizden emin misiniz?')) return;
    try {
      await adminRoutesApi.delete(id);
      fetchRoutes();
    } catch (error) {
      console.error('Failed to delete route:', error);
    }
  };

  const resetForm = () => {
    setEditingRoute(null);
    setFormData({
      name: '',
      description: '',
      duration: '',
      places_count: 0,
      image: '',
      popular: true,
      category: '',
      rating: 4.5
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Rotalar</h1>
          <p className="text-slate-400 mt-1">Toplam {total} rota</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-purple-500 hover:bg-purple-600 text-white"
        >
          <Plus className="w-5 h-5 mr-2" />
          Yeni Rota
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <Input
          type="text"
          placeholder="Rota ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      ) : routes.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          Rota bulunamadı
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {routes.map((route) => (
            <div key={route.id} className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden group hover:border-slate-600 transition-all">
              <div className="relative h-48">
                <img src={route.image} alt={route.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                {route.popular && (
                  <div className="absolute top-3 left-3 px-2 py-1 bg-purple-500 rounded-full text-xs font-medium text-white">
                    Popüler
                  </div>
                )}
                <div className="absolute bottom-3 left-3 right-3">
                  <h3 className="text-white font-semibold text-lg">{route.name}</h3>
                  <div className="flex items-center gap-3 mt-2 text-sm text-slate-300">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> {route.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> {route.places_count} durak
                    </span>
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" /> {route.rating}
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <p className="text-slate-400 text-sm line-clamp-2">{route.description}</p>
                <div className="flex items-center justify-end gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(route)}
                    className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(route.id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800">
              <h3 className="text-lg font-semibold text-white">
                {editingRoute ? 'Rota Düzenle' : 'Yeni Rota'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Rota Adı</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Görsel URL</label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  required
                  placeholder="https://..."
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Süre</label>
                  <Input
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    required
                    placeholder="Örn: 1 gün"
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Durak Sayısı</label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.places_count}
                    onChange={(e) => setFormData({ ...formData, places_count: parseInt(e.target.value) })}
                    required
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Kategori</label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="Örn: Tarihi, Doğa"
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Puan</label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: parseFloat(e.target.value) })}
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="popular"
                  checked={formData.popular}
                  onChange={(e) => setFormData({ ...formData, popular: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-purple-500 focus:ring-purple-500"
                />
                <label htmlFor="popular" className="text-slate-300">Popüler rota</label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={actionLoading}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Kaydet'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRoutesPage;
